import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { convertTrackers, SmeTrackerSummary } from '../../api/batch3Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one daily tracker workbook (.xlsx), or several.',
  'With several files, choose Merge All (one consolidated workbook, Team = file name) or Batch Process (one output per file, zipped).',
  'Click Convert to Template. The converted file downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

type Mode = 'merge' | 'batch';

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

function SmeTrackerPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [mode, setMode] = useState<Mode>('merge');
  const { status, error, result, fail, run } = useFileRun<FileResult<SmeTrackerSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload at least one daily tracker file.');
      return;
    }
    await run(() => convertTrackers(files, mode), 'Conversion failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Supported sheets</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>IP sheets: Received, Processed, Pending, Cumulative Pending, Remarks.</li>
          <li>Bank / BR sheets: Opening, Received, Processed, Query, Closing.</li>
          <li>Other sheets: particulars are detected from your data. Dates in any format are supported.</li>
        </ul>
      </div>

      <FileDrop
        label="Daily tracker files"
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      {files.length > 1 && (
        <div className="mb-6 rounded-lg border border-border bg-muted p-4">
          <Label htmlFor="sme-mode" className="mb-1.5">
            Processing mode
          </Label>
          <select id="sme-mode" className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            <option value="merge">Merge All (single consolidated output)</option>
            <option value="batch">Batch Process (separate outputs, zipped)</option>
          </select>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Converting…' : 'Convert to Template'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Converting…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {s?.mode === 'single' && (
            <p className="mt-1 text-xs text-green-800">
              {s.sheets_processed}/{s.total_sheets} sheets processed.
            </p>
          )}
          {s?.mode === 'merge' && (
            <div className="mt-1 text-xs text-green-800">
              <p>
                {s.input_files} files · {s.total_clients} clients · {s.date_range} dates · {s.sheets_processed} sheets
              </p>
              <ul className="mt-1 list-disc pl-5">
                {Object.entries(s.teams ?? {}).map(([team, file]) => (
                  <li key={team}>
                    {team} ← {file}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s?.mode === 'batch' && (
            <ul className="mt-1 list-disc pl-5 text-xs text-green-800">
              {s.files?.map((f) => (
                <li key={f.output}>
                  {f.file}: {f.sheets_processed}/{f.total_sheets} sheets
                </li>
              ))}
            </ul>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload your daily tracker file(s) above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default SmeTrackerPanel;
